import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';

// Consts
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class GoogleMapsApiLoaderService {

  private apiLoaded: BehaviorSubject<boolean> = new BehaviorSubject(null);

  private apiLoaded$: Observable<boolean> = this.apiLoaded.asObservable().pipe(
    filter<boolean>(Boolean),
  );

  private isLoading: boolean = false;

  // State of 3D library
  private api3dLoaded: BehaviorSubject<boolean> = new BehaviorSubject(null);

  private api3dLoaded$: Observable<boolean> = this.api3dLoaded.asObservable().pipe(
    filter<boolean>(Boolean),
  );

  private is3dLoading: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  init(): void {
    if (isPlatformServer(this.platformId)) { return; }

    if (this.isLoading || this.apiLoaded.value) { return; }

    this.isLoading = true;
    this.loadGoogleMapsApi();
  }

  load(): Observable<boolean> {
    if (isPlatformServer(this.platformId)) { return of(false); }

    if (!this.apiLoaded.value) this.init();

    return this.apiLoaded$;
  }

  // Will be needed after Googles official release of maps 3d
  load3dMap(): Observable<boolean> {
    if (isPlatformServer(this.platformId)) { return of(false); }

    if (this.api3dLoaded.value || this.is3dLoading) {
      return this.api3dLoaded$;
    }

    this.is3dLoading = true;

    this.load().subscribe(async (isBaseReady: boolean) => {
      if (isBaseReady) {
        try {
          await google.maps.importLibrary('maps3d');
          this.is3dLoading = false;
          this.api3dLoaded.next(true);
        } catch (error) {
          console.error('GoogleMapsApiLoaderService: Failed to load maps3d library', error);
          this.is3dLoading = false;
          this.api3dLoaded.next(false);
        }
      }
    });

    return this.api3dLoaded$;
  }

  private loadGoogleMapsApi(): void {
    const script: HTMLScriptElement = document.createElement('script');

    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.mapsApiKey}&libraries=places,marker&language=en-US&loading=async`;
    script.async = true;
    script.defer = true;

    script.onload = (): void => {
      this.waitForApiReady();
    };

    script.onerror = (): void => {
      console.error('GoogleMapsApiLoaderService: Failed to load Google Maps API');
      this.isLoading = false;
      this.apiLoaded.next(false);
    };

    document.head.appendChild(script);
  }

  private waitForApiReady(): void {
    const checkReady = (): void => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places && google.maps.marker) {
        this.isLoading = false;
        this.apiLoaded.next(true);
      } else {
        // Check again after a short delay
        setTimeout(checkReady, 50);
      }
    };

    checkReady();
  }

}
